import * as THREE from "three";
import { Cell, CellKind, Grid } from "@highmarch/domain";
import { Palette, cellToWorld } from "../scene";
import { HQ_GRASS, HQ_YAW, cellHash, cube, edgeTile, lib } from "./common";
import { FoliageView } from "./foliage";
import { Terrain } from "../terrain";
import { GroundMesh } from "../groundMesh";
import { FORTRESS_SEAT, GroundContacts } from "../groundSlope";

/** Weights for the grass variants g_grass0..5 (plain, meadow, flowers, dirt patch, mushrooms, rubble), art/hq/README.md. */
const GRASS_WEIGHTS = [64, 14, 4, 9, 4, 5];

/** Static grid: grass tiles by weighted variant with random yaw, cliffs on the island edge, blockers and dressing (FoliageView). Road cells hide their grass tile and dressing. */
export class GridView {
  readonly group = new THREE.Group();
  readonly foliage = new FoliageView();
  private readonly tiles = new Map<number, THREE.Object3D>();
  constructor(private readonly grid: Grid) {
    const L = lib();
    this.group.add(this.foliage.group);
    this.foliage.setBase(grid);
    // The fortress's contact shadow on the ground: its whole footprint, a little inside the walls, with the soft
    // falloff running out past them. It is what seats the keep on the grass without GTAO (lighting F4b).
    const fp = grid.footprint;
    if (fp.length) {
      const xs = fp.map((c) => c.x), zs = fp.map((c) => c.y);
      const x0 = Math.min(...xs), x1 = Math.max(...xs), z0 = Math.min(...zs), z1 = Math.max(...zs);
      GroundContacts.add({ x: (x0 + x1) / 2, z: (z0 + z1) / 2, hx: (x1 - x0 + 1) / 2 + FORTRESS_SEAT, hz: (z1 - z0 + 1) / 2 + FORTRESS_SEAT, r: 0.6, s: 0.5 });
    }
    for (let i = 0; i < grid.cellCount; i++) {
      const c = grid.cellAt(i);
      const kind = grid.kindAt(c);
      const T = Terrain.current;
      if (L && GroundMesh.available && T) {
        // the continuous ground mesh draws the surface and the cliffs; only the discrete pieces stay as models
        // the lava basin is the ground mesh's own recessed molten surface now, so only the chasm floor is a model
        // the ground mesh now builds the chasm floor as well as the molten surface, so neither is a model any more
        const pit = false, lava = false;
        if (pit || lava) {
          const tile = L.clone(`hq:${pit ? "g_gap" : "g_lava"}`);
          tile.position.copy(cellToWorld(c, pit ? T.floorAt(c.x, c.y) : T.groundOf(c)));
          tile.rotation.y = HQ_YAW;
          this.group.add(tile);
          this.tiles.set(i, tile);
        } else if (T.isLava(c) || T.isPit(c)) {
          // a chasm or a lava basin is a hole in the ground; planting a boulder in it looks like a mistake
        } else if (kind !== CellKind.Blocked && kind !== CellKind.Base) this.foliage.addDressing(c, i);
        else if (kind === CellKind.Blocked) this.foliage.addBlocker(c, i);
        continue;
      }
      if (L && L.hq("g_grass0")) {
        const h = cellHash(c);
        // fallback tile path (the terrain maps did not ship): flat tiles, with the island's rim as the only cliff
        const edge = edgeTile(grid, c, { n: false, e: false, s: false, w: false });
        const cliff = edge !== null && L.hq(edge.name);
        const pit = T?.isPit(c) && L.hq("g_gap"), lava = T?.isLava(c) && L.hq("g_lava");
        const tile = L.clone(`hq:${pit ? "g_gap" : lava ? "g_lava" : cliff ? edge!.name : grassVariant(h)}`);
        tile.position.copy(cellToWorld(c, 0));
        tile.rotation.y = HQ_YAW + (cliff ? edge!.yaw : (h % 4) * (Math.PI / 2));
        this.group.add(tile);
        this.tiles.set(i, tile);
        if (kind === CellKind.Blocked) { if (!pit && !lava) this.foliage.addBlocker(c, i); }
        else if (kind !== CellKind.Base) this.foliage.addDressing(c, i); // the fortress covers its six cells
      } else {
        // no terrain maps and no HQ grass (or no library at all): plain ground slabs, blocked cells as plain blocks
        const g = cube(cellToWorld(c, -0.05), 0.96, 0.1, 0.96, Palette.ground);
        this.group.add(g);
        this.tiles.set(i, g);
        if (kind === CellKind.Blocked) this.group.add(cube(cellToWorld(c, 0.3), 0.9, 0.6, 0.9, Palette.blocked));
      }
    }
    this.foliage.bakeMap();
  }

  /**
   * Hide the plain ground slab (when the road is drawn as plain blocks too) and the dressing under road cells, and keep tall props clear of the portal and
   * the first road cells (FoliageView.PortalClear). `cells` is the road in walking order, the portal first.
   */
  setRoad(cells: readonly Cell[]): void {
    for (const t of this.tiles.values()) t.visible = true;
    this.foliage.setRoad(cells.map((c) => this.grid.index(c)), cells);
    const L = lib();
    if (L && L.hq("g_path_straight")) return; // HQ path pieces carry their own grass edges and sit on the grass tile
    for (const c of cells) { const t = this.tiles.get(this.grid.index(c)); if (t) t.visible = false; }
  }

  update(dt: number): void { this.foliage.update(dt); }

  /** Frees what this view built for itself (the baked scenery); the library's models are shared and stay. */
  dispose(): void { this.foliage.dispose(); }
}

/** Weighted pick among the grass variants the set actually has. */
function grassVariant(h: number): string {
  const L = lib()!;
  const have = HQ_GRASS.map((n, i) => [n, GRASS_WEIGHTS[i] ?? 5] as const).filter(([n]) => L.hq(n));
  const total = have.reduce((s, [, w]) => s + w, 0);
  let r = (h >> 2) % total;
  for (const [n, w] of have) { if (r < w) return n; r -= w; }
  return have[0][0];
}
